import * as React from "react";
import {
  MenuItem,
  Rating,
  Select,
  Stack,
  ToggleButton,
  ToggleButtonGroup,
  Grid,
  Typography,
} from "@mui/material";

import { IXProductDetailsProps } from "../../../../utils/interfaces/containers/XProductDetails.interfaces";

import { IArticleData } from "../../../../utils/interfaces/common/XArticle.interfaces";
import DetailsGroup from "./DetailsGroup";
import DetailsWrapper from "./DetailsWrapper";
import { TSetUrlParamsArgs } from "../../../../hooks/useXoraia";
import QuantitySelector from "./QuantitySelector";

const ProductDetailsInfo: React.FC<
  IXProductDetailsProps & {
    article: IArticleData;

    onChange: (args: TSetUrlParamsArgs) => Promise<void>;

    urlParams?: TSetUrlParamsArgs;
  }
> = ({
  previewMode = "grid",
  previewPosition = "left",
  article,
  onChange,
  urlParams = {},
  ...props
}) => {
  const {
    detailsGroupMode = "column",
    // REVIEWS
    showReviews = true,
    starCountMode = "full",
  } = props;

  return (
    <Grid
      item
      xs={12}
      md={previewPosition === "top" ? 12 : previewMode === "list" ? 7 : 6}
    >
      <DetailsWrapper {...props}>
        <Typography className="XProductDetails_Name">{article.name}</Typography>
        {/* // todo use currentPrice & oldPrice instead of price and discount */}
        <Typography className="XProductDetails_Price">
          {article.price} F CFA
          {!!article.discount && (
            <Typography
              className="XProductDetails_Discount"
              component="span"
              ml={1}
            >
              {article.discount} F CFA
            </Typography>
          )}
        </Typography>
        {showReviews && (
          <Stack direction="row" spacing={1} alignItems="center">
            <Rating
              className="XProductDetails_Rating"
              size="small"
              precision={0.5}
              readOnly
              // TODO ADD OPTION TO GET FINAL RATE
              // AFTER REVIEW
              value={article.initialRate}
            />
            {/* // todo get rate count dynamically */}
            {starCountMode !== "none" && (
              <Typography variant="caption">
                {starCountMode === "full" ? `(0 Notes)` : `(0)`}
              </Typography>
            )}
          </Stack>
        )}
        <DetailsGroup title="DESCRIPTION" mode={detailsGroupMode}>
          <Typography component="span">{article.description}</Typography>
        </DetailsGroup>
        {/* // todo calculate the final qty */}
        <DetailsGroup title="QUANTITE" mode={detailsGroupMode}>
          <Typography component="span">
            {article.isInfinite ? "Disponible" : article.qty}
          </Typography>
        </DetailsGroup>
        {article.options &&
          article.options.map((opt) => {
            const value = urlParams[opt.id] ?? opt.values[0];

            return (
              <DetailsGroup
                key={opt.id}
                title={opt.name.toUpperCase()}
                mode={detailsGroupMode}
              >
                {opt.type === "COLOR" && (
                  <ToggleButtonGroup
                    exclusive
                    value={value}
                    onChange={async (_, color) =>
                      await onChange({ [opt.id]: color })
                    }
                    className="XProductDetails_ColorPicker"
                  >
                    {opt.values.map((v) => (
                      <ToggleButton value={v} key={v}>
                        <Stack
                          className="XProductDetails_ColorPicker_Color"
                          bgcolor={v}
                          width="100%"
                          height="100%"
                        />
                      </ToggleButton>
                    ))}
                  </ToggleButtonGroup>
                )}

                {opt.type === "CHOICE" && (
                  <ToggleButtonGroup
                    exclusive
                    value={value}
                    className="XProductDetails_ToggleBtn"
                    onChange={async (_, choice) =>
                      await onChange({ [opt.id]: choice })
                    }
                  >
                    {opt.values.map((v) => (
                      <ToggleButton value={v} key={v}>
                        {v}
                      </ToggleButton>
                    ))}
                  </ToggleButtonGroup>
                )}

                {opt.type === "SELECT" && (
                  <Select
                    value={value}
                    size="small"
                    className="XProductDetails_SelectOption"
                    onChange={async (e) =>
                      await onChange({ [opt.id]: e.target.value })
                    }
                  >
                    {opt.values.map((v) => (
                      <MenuItem key={v} value={v}>
                        {v}
                      </MenuItem>
                    ))}
                  </Select>
                )}
              </DetailsGroup>
            );
          })}
        <QuantitySelector
          {...props}
          onChange={onChange}
          urlParams={urlParams}
          article={article}
        />
        {/* // TODO IMPLEMENT THIS WITH STREAM CHECKER */}
        {/* <Button
          color="error"
          startIcon={<FavoriteOutlined />}
          sx={{ "&:hover": { bgcolor: "transparent" } }}
        >
          Ajouter aux favories
        </Button> */}
      </DetailsWrapper>
    </Grid>
  );
};

export default ProductDetailsInfo;
